import moment from "moment/moment";
import { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../../../../context/languageContext";
import GTM from "../../../../util/tracking/gtm";
import { ApplicationFlowSteps } from "./types";
import ProductContextHelper from "../../../../context/product-context/helper";
import {
  BasicAccountApplicationDocument,
  CardApplicationDocument,
} from "../../../../api/commercial/generated-types";
import { ProductRefType } from "../../../../context/product-context/types";

function isCardApplicationStep(step: ApplicationFlowSteps) {
  return (
    step === ApplicationFlowSteps.CARDHOLDER_BENEFITS ||
    step === ApplicationFlowSteps.CHOOSE_SIGNATORIES ||
    step === ApplicationFlowSteps.SUMMARY ||
    step === ApplicationFlowSteps.SIGN
  );
}

function resolveProduct(
  basicAccountApplication: BasicAccountApplicationDocument,
  cardApplication: CardApplicationDocument,
): ProductRefType {
  if (basicAccountApplication) {
    return ProductContextHelper.productRefToProduct(
      basicAccountApplication.productRef,
    );
  }

  if (cardApplication) {
    return ProductContextHelper.productRefToProduct(cardApplication.productRef);
  }

  return ProductRefType.SWISS_SILVER;
}

function resolveStep(
  basicAccountApplication: BasicAccountApplicationDocument,
  cardApplication: CardApplicationDocument,
  noValidApplications: boolean,
): ApplicationFlowSteps {
  if (noValidApplications) {
    return ApplicationFlowSteps.COMPANY;
  }

  if (basicAccountApplication && cardApplication) {
    // take the most recently modified application step when the two applications exist
    const baaUpdatedTime = moment(basicAccountApplication.lastModifiedDate);
    const caUpdatedTime = moment(cardApplication.lastModifiedDate);
    return baaUpdatedTime.isAfter(caUpdatedTime)
      ? (basicAccountApplication.step as ApplicationFlowSteps)
      : (cardApplication.step as ApplicationFlowSteps);
  } else if (basicAccountApplication) {
    return isCardApplicationStep(
      basicAccountApplication.step as ApplicationFlowSteps,
    )
      ? ApplicationFlowSteps.CARDHOLDER
      : (basicAccountApplication.step as ApplicationFlowSteps);
  } else if (cardApplication) {
    return cardApplication.step as ApplicationFlowSteps;
  }
}

export default function useStep(
  basicAccountApplication: BasicAccountApplicationDocument,
  cardApplication: CardApplicationDocument,
  noValidApplications: boolean,
): [ApplicationFlowSteps, (step: ApplicationFlowSteps) => void] {
  const { language } = useContext(LanguageContext);
  const initialStep = resolveStep(
    basicAccountApplication,
    cardApplication,
    noValidApplications,
  );
  const [step, setStep] = useState(initialStep);
  const [product, setProduct] = useState(ProductRefType.SWISS_SILVER);

  useEffect(() => {
    const resolvedStep = resolveStep(
      basicAccountApplication,
      cardApplication,
      noValidApplications,
    );

    const resolvedProduct: ProductRefType = resolveProduct(
      basicAccountApplication,
      cardApplication,
    );

    if (resolvedProduct && resolvedStep) {
      setProduct(resolvedProduct);
      setStep(resolvedStep);
      GTM.trackVirtualPageView(resolvedStep, resolvedProduct, language);
    }
  }, [basicAccountApplication, cardApplication, language, noValidApplications]);

  const setStepAndTrack = (currentStep: ApplicationFlowSteps) => {
    setStep(currentStep);
    GTM.trackVirtualPageView(currentStep, product, language);
  };

  return [step, setStepAndTrack];
}
